import { group } from 'd3-array';

// climate indicators for the heat component set, matched to indicators.csv by variable name
// order is the order of the climate indicator picker, matching the metadata order column
// default: marks the indicator the picker loads first, wherever it sits in this list
// legend: labels for the top (high) and bottom (low) of the percentile legend
// explainer (optional): replaces the whole sentence the Climate + Community Details panel
// writes from the plain definition. Placeholders {place}, {value}, {median} and {period} are
// filled in, and {value} and {median} are emphasised. Units are written into the template
// rather than taken from the metadata, so each one reads the way the client asked for it. An
// explainer with no {median} drops the state median sentence, which is deliberate for the
// indicators measured against a federal standard instead.
const climateIndicators = [
  // air quality: concentration measures rather than counts, so the legend reads higher/lower.
  // CalEnviroScreen originally shipped these three with the percentile inverted, so a high
  // percentile meant the cleanest air. That was corrected in the data in October 2026, and
  // these labels depend on the corrected direction.
  {
    varname: 'dieselpm',
    legend: { high: 'Higher', low: 'Lower' },
    explainer:
      'This {place} experiences {value} tons per year of cumulative diesel particulate matter ' +
      'emissions. The median value for the state is {median} tons per year.',
  },
  {
    varname: 'ozone',
    legend: { high: 'Higher', low: 'Lower' },
    explainer:
      'This {place} experiences a mean ozone concentration of {value} parts per million. The ' +
      'USEPA standard for mean ozone is 0.070 parts per million.',
  },
  {
    varname: 'pm25',
    legend: { high: 'Higher', low: 'Lower' },
    explainer:
      'This {place} experiences a particulate matter (PM2.5) concentration of {value} µg/m³. ' +
      'The US EPA standard for annual average fine particulate matter is 9.0 µg/m³.',
  },
  // wildfire smoke already runs the usual way round: the high percentile is the most smoke
  {
    varname: 'wildfire_smoke',
    legend: { high: 'Higher', low: 'Lower' },
    explainer:
      'This {place} is expected to experience wildfire smoke concentrations of {value} ' +
      'µg/m³ in {period} under a high greenhouse gas emissions scenario (SSP3-7.0). The ' +
      'median value for the state is {median} µg/m³.',
  },
  { varname: 'drought', default: true, legend: { high: 'Drier', low: 'Wetter' } },
  { varname: 'hhe_freq', legend: { high: 'More events', low: 'Fewer events' } },
  { varname: 'DaysAbove100F', legend: { high: 'More days', low: 'Fewer days' } },
  { varname: 'DaysAbove90F', legend: { high: 'More days', low: 'Fewer days' } },
  { varname: 'DaysAboveBaseline', legend: { high: 'More days', low: 'Fewer days' } },
  { varname: 'hhe_dur', legend: { high: 'Longer events', low: 'Shorter events' } },
  { varname: 'WarmNights', legend: { high: 'More nights', low: 'Fewer nights' } },
  { varname: 'ffwi_2010_2039', legend: { high: 'More days', low: 'Fewer days' } },
];

export const getClimateIndicatorConfig = indicator => {
  // stratified slices copy their parent's varname, so skip them
  if (!indicator || !indicator.varname || indicator.parentIndicator) return undefined;
  const varname = indicator.varname.toLowerCase();
  return climateIndicators.find(c => c.varname.toLowerCase() === varname);
};

export const isClimateIndicator = indicator => getClimateIndicatorConfig(indicator) !== undefined;

// a climate indicator with more than one time period. every indicator with several dates gets
// period selectors, but only these get the period chart in Climate + Community Details
export const hasClimatePeriods = indicator =>
  isClimateIndicator(indicator) && Boolean(indicator.dates) && indicator.dates.length > 1;

// the climate indicators found in a flat list of indicators, in config order
export const getClimateIndicators = indicators =>
  climateIndicators
    .map(c => indicators.find(i => getClimateIndicatorConfig(i) === c))
    .filter(Boolean);

// the same, grouped by the part of the title before the colon, e.g. [['Extreme Heat', [...]], ...]
export const getClimateIndicatorGroups = indicators =>
  Array.from(
    group(getClimateIndicators(indicators), i => i.title.substring(0, i.title.indexOf(':')))
  );

// the climate indicator the heat component set maps on load: the one the config marks as the
// default, or else the first one in config order
export const getDefaultClimateIndicator = indicators => {
  const found = getClimateIndicators(indicators);
  const marked = climateIndicators.find(c => c.default);
  return (marked && found.find(i => getClimateIndicatorConfig(i) === marked)) || found[0];
};

// the default time period for an indicator with several: the heat component set uses the
// period covering the present (the first one ending this year or later), others use the latest
export const getDefaultYear = (dates, componentSet) => {
  const years = dates.map(d => +d).sort((a, b) => a - b);
  const latest = years[years.length - 1];
  if (componentSet !== 'heat') return latest;
  const thisYear = new Date().getFullYear();
  const current = years.find(y => y >= thisYear);
  return current === undefined ? latest : current;
};

export default climateIndicators;
